import {
  Accordion,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowLeftBar,
  IconBrandWhatsapp,
  IconCheck,
  IconClipboardText,
  IconFileText,
  IconMapPin,
  IconStarFilled,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import GetHelpForm from "./Shared/GetHelpForm";
import Seo from "../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../seo/utils";
import { buildWhatsAppUrl } from "../Services/contact.service";
import { formatReviewCount, getReviewSummary } from "../Services/review.service";
import {
  cityPages,
  featuredCountryCityGroups,
  getCityPageBySlug,
} from "../data/cityPages";
import "./Services/ServiceLandingPage.css";
import "./CityPage.css";

function CityPage() {
  const theme = useMantineTheme();
  const desktopMediaQuery = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { country, city } = useParams<{ country: string; city: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [city, location.pathname]);

  const page = getCityPageBySlug(city?.toLowerCase(), country?.toLowerCase());
  const reviewSummary = getReviewSummary();

  if (!page) {
    return (
      <Container py="xl">
        <Stack gap="md">
          <Seo
            title="City Page Not Found | SubjectBuddy"
            description="The city assignment help page you requested could not be found."
            path={location.pathname}
            noIndex
          />
          <Title order={2}>City page not found</Title>
          <Button variant="subtle" onClick={() => navigate("/")}>
            Back to home
          </Button>
        </Stack>
      </Container>
    );
  }

  const whatsappUrl = buildWhatsAppUrl(
    `Hi, I need help with ${page.city} assignment support.`,
  );

  return (
    <Container fluid mt={desktopMediaQuery ? "md" : 50} className="service-page">
      <Seo
        title={page.pageTitle}
        description={page.seoDescription}
        path={location.pathname}
        keywords={page.keywords}
        schema={[
          createOrganizationSchema(),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: page.heroTitle,
            description: page.seoDescription,
            brand: {
              "@type": "EducationalOrganization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: reviewSummary.averageRating.toFixed(1),
              ratingCount: String(reviewSummary.totalReviews),
            },
            url: buildCanonicalUrl(location.pathname),
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.heroTitle,
            description: page.seoDescription,
            areaServed: {
              "@type": "City",
              name: page.city,
              containedInPlace: {
                "@type": "Country",
                name: page.country,
              },
            },
            serviceType: page.keywords[0],
            provider: {
              "@type": "Organization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            url: buildCanonicalUrl(location.pathname),
          },
          createFaqSchema(page.faqs),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: page.country, path: `/${page.countrySlug}` },
            { name: page.city, path: page.path },
          ]),
        ]}
      />

      <div className="service-layout">
        <Stack gap={26} className="service-main">
          <Group justify="space-between">
            <Button
              color="blue"
              variant="subtle"
              size={desktopMediaQuery ? "lg" : "sm"}
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate("/")}
              pl={0}
            >
              Back
            </Button>
            <Text size="sm" fw={600} c="dimmed">
              Target keyword: {page.keywords[0]}
            </Text>
          </Group>

          <Paper
            radius="xl"
            p={desktopMediaQuery ? "3rem" : "1.25rem"}
            className="service-hero-card city-hero"
          >
            <Stack gap="lg">
              <Group gap="sm">
                <Badge color="green" size="lg" radius="sm" leftSection={<IconMapPin size={14} />}>
                  {page.city}
                </Badge>
                <Badge color="blue" size="lg" radius="sm" variant="light">
                  Assignment Help {page.countryCode}
                </Badge>
              </Group>
              <Title
                order={1}
                c={theme.colors.green[9]}
                style={{
                  fontSize: desktopMediaQuery ? "3rem" : "2rem",
                  lineHeight: 1.08,
                  letterSpacing: "-0.03em",
                }}
              >
                {page.heroTitle}
              </Title>
              <Text size={desktopMediaQuery ? "lg" : "md"} c="dimmed" maw={760}>
                {page.heroDescription}
              </Text>
              <Group gap="sm">
                <Button
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  radius="lg"
                  leftSection={<IconBrandWhatsapp />}
                >
                  Get Assignment Help
                </Button>
                <Button
                  component={Link}
                  to="/assignment-help"
                  variant="light"
                  radius="lg"
                  leftSection={<IconFileText />}
                >
                  Explore Subjects
                </Button>
              </Group>
              <Divider />
              <Box className="service-rich-text">
                <p>{page.intro}</p>
              </Box>
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
                <Paper withBorder radius="lg" p="lg" className="city-stat">
                  <Text fw={800} size="xl">3000+</Text>
                  <Text size="sm" c="dimmed">Academic experts</Text>
                </Paper>
                <Paper withBorder radius="lg" p="lg" className="city-stat">
                  <Group gap={4}>
                    <Text fw={800} size="xl">
                      {reviewSummary.averageRating.toFixed(1)}
                    </Text>
                    <IconStarFilled size={18} color={theme.colors.yellow[6]} />
                  </Group>
                  <Text size="sm" c="dimmed">Student rating</Text>
                </Paper>
                <Paper withBorder radius="lg" p="lg" className="city-stat">
                  <Text fw={800} size="xl">
                    {formatReviewCount(reviewSummary.totalReviews)}
                  </Text>
                  <Text size="sm" c="dimmed">Verified student reviews</Text>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              <Title order={2}>How Our Expert Online Assignment Help Works in {page.city}</Title>
              <SimpleGrid cols={{ base: 1, md: 4 }} spacing="lg">
                {page.process.map((step, index) => (
                  <Stack key={step.title} gap="sm" className="city-process-step">
                    <ThemeIcon size={42} radius="xl" color={index % 2 ? "blue" : "green"}>
                      <IconClipboardText size={22} />
                    </ThemeIcon>
                    <Text fw={800}>{step.title}</Text>
                    <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                      {step.description}
                    </Text>
                  </Stack>
                ))}
              </SimpleGrid>
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              <Title order={2}>Types of Assignment Help Services in {page.city}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                From short essays to technical projects, SubjectBuddy helps {page.city} students turn confusing instructions into a clear academic plan.
              </Text>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Paper withBorder radius="lg" p="lg">
                  <Stack gap="sm">
                    <Title order={3}>What this support helps with</Title>
                    <List
                      spacing="sm"
                      icon={
                        <ThemeIcon color="green" size={20} radius="xl">
                          <IconCheck size={14} />
                        </ThemeIcon>
                      }
                    >
                      {page.outcomes.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Paper>
                <Paper withBorder radius="lg" p="lg">
                  <Stack gap="sm">
                    <Title order={3}>Support focus areas</Title>
                    <List
                      spacing="sm"
                      icon={
                        <ThemeIcon color="blue" size={20} radius="xl">
                          <IconCheck size={14} />
                        </ThemeIcon>
                      }
                    >
                      {page.features.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Paper>
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                {page.serviceTypes.map((serviceType) => (
                  <Paper key={serviceType} withBorder radius="md" p="md" className="city-service-pill">
                    <Group gap="sm" wrap="nowrap">
                      <ThemeIcon color="green" variant="light" radius="xl" size={28}>
                        <IconCheck size={16} />
                      </ThemeIcon>
                      <Text fw={700} size="sm">{serviceType}</Text>
                    </Group>
                  </Paper>
                ))}
              </SimpleGrid>
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              {page.contentSections.map((section) => (
                <Box key={section.heading} className="service-rich-text">
                  <h2>{section.heading}</h2>
                  <p>{section.body}</p>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              <Title order={2}>More {page.country} City Assignment Help</Title>
              <Text c="dimmed">
                Explore other major education cities in {page.country} if you want local support from a different location.
              </Text>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                {cityPages
                  .filter(
                    (otherPage) =>
                      otherPage.countrySlug === page.countrySlug &&
                      otherPage.slug !== page.slug,
                  )
                  .slice(0, 8)
                  .map((otherPage) => (
                    <Paper
                      key={otherPage.slug}
                      component={Link}
                      to={otherPage.path}
                      withBorder
                      radius="lg"
                      p="lg"
                      style={{
                        cursor: "pointer",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <Text size="sm" tt="uppercase" fw={700} c="blue">
                        {otherPage.city}
                      </Text>
                      <Text fw={700}>{otherPage.pageTitle}</Text>
                      <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                        {otherPage.description}
                      </Text>
                    </Paper>
                  ))}
              </SimpleGrid>
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              <Title order={2}>Assignment Help By Country</Title>
              <Text c="dimmed">
                SubjectBuddy supports students across top international education destinations and their most searched academic cities.
              </Text>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                {featuredCountryCityGroups.map((group) => (
                  <Paper key={group.countrySlug} withBorder radius="lg" p="lg">
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start">
                        <Text fw={800}>{group.country}</Text>
                        <Badge variant="light" color="green" radius="sm">
                          {group.cities.length} cities
                        </Badge>
                      </Group>
                      <Group gap="xs">
                        {group.cities.map((cityPage) => (
                          <Button
                            key={cityPage.path}
                            component={Link}
                            to={cityPage.path}
                            variant={cityPage.path === page.path ? "filled" : "light"}
                            color={cityPage.path === page.path ? "green" : "blue"}
                            size="xs"
                            radius="md"
                          >
                            {cityPage.city}
                          </Button>
                        ))}
                      </Group>
                    </Stack>
                  </Paper>
                ))}
              </SimpleGrid>
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              <Title order={2}>SubjectBuddy: Frequently Asked Questions</Title>
              <Accordion variant="separated" radius="md">
                {page.faqs.map((faq) => (
                  <Accordion.Item key={faq.question} value={faq.question}>
                    <Accordion.Control>
                      <Text fw={700}>{faq.question}</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                        {faq.answer}
                      </Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Stack>
          </Paper>
        </Stack>

        <aside className="service-sidebar">
          <div className="service-sidebar-sticky">
            <Stack gap="md">
              <Title order={desktopMediaQuery ? 3 : 4}>Need Help With This?</Title>
              <Paper
                withBorder
                radius="lg"
                p={0}
                className="city-sidebar-form-panel"
              >
                <GetHelpForm formSize="sm" flatForm />
              </Paper>
              <Paper withBorder radius="lg" p={desktopMediaQuery ? "xl" : "md"}>
                <Text fw={700}>Quick support</Text>
                <Text c="dimmed" size="sm">
                  Share your brief, module, and deadline to get faster academic support tailored for your city.
                </Text>
                <Button
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  radius="lg"
                  leftSection={<IconBrandWhatsapp />}
                >
                  Message us on WhatsApp
                </Button>
              </Paper>
            </Stack>
          </div>
        </aside>
      </div>
    </Container>
  );
}

export default CityPage;
